import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../supabaseClient'
import AdminIdentityCleanupPanel from '../AdminIdentityCleanupPanel'
import AnnouncementsCenter from '../AnnouncementsCenter'
import EmptyState from '../ui/EmptyState'
import ErrorState from '../ui/ErrorState'
import LoadingState from '../ui/LoadingState'
import SectionHeader from '../ui/SectionHeader'
import StatPill from '../ui/StatPill'
import AdminAuditReportsSettingsPanel from './AdminAuditReportsSettingsPanel'
import AdminIssuesPanel from './AdminIssuesPanel'
import ContactsAdminPanel from './ContactsAdminPanel'
import CorrectionCenterPanel from './CorrectionCenterPanel'
import PayStatusAdminPanel from './PayStatusAdminPanel'
import { isSchemaMismatchError } from './schemaGuard'
import {
  ADMIN_SECTION_KEYS,
  canAccessAdminOps,
  canAccessAdminSection,
  formatAccessRoleLabel,
  getDefaultAdminSectionForRole,
  getAdminSectionPermissions,
  normalizeAccessRoleValue
} from './adminConfig'

const formatDateTime = (value) => {
  if (!value) return 'Unknown'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Unknown'
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

const SECTION_LABELS = {
  [ADMIN_SECTION_KEYS.DASHBOARD]: 'Dashboard',
  [ADMIN_SECTION_KEYS.ACCESS]: 'Access',
  [ADMIN_SECTION_KEYS.WORKFORCE]: 'Workers / Jobs / Sites',
  [ADMIN_SECTION_KEYS.CONTACTS]: 'Contacts',
  [ADMIN_SECTION_KEYS.TIME_PAY]: 'Time + Pay',
  [ADMIN_SECTION_KEYS.ISSUES]: 'Issues',
  [ADMIN_SECTION_KEYS.CORRECTIONS]: 'Corrections',
  [ADMIN_SECTION_KEYS.AUDIT]: 'Audit',
  [ADMIN_SECTION_KEYS.REPORTS]: 'Reports',
  [ADMIN_SECTION_KEYS.SETTINGS]: 'Settings',
  [ADMIN_SECTION_KEYS.ANNOUNCEMENTS]: 'Announcements'
}

export default function AdminOperationsCenter({
  accessRole = '',
  workspaceRole = '',
  userWorkerId = '',
  requestedSection = '',
  onOpenWorkers,
  onOpenJobs,
  onOpenSites,
  onOpenManagerTime,
  onOpenIssueCenter,
  onOpenAnnouncements,
  onOpenAccessTools
}) {
  const normalizedAccessRole = normalizeAccessRoleValue(accessRole)
  const [activeSection, setActiveSection] = useState(getDefaultAdminSectionForRole(normalizedAccessRole))

  const [dashboardLoading, setDashboardLoading] = useState(true)
  const [dashboardError, setDashboardError] = useState('')
  const [dashboardUpdatedAt, setDashboardUpdatedAt] = useState('')
  const [dashboardStats, setDashboardStats] = useState({
    todaysJobs: 0,
    scheduledWorkers: 0,
    clockedInNow: 0,
    missingPunches: 0,
    hoursAwaitingReview: 0,
    unresolvedIssues: 0,
    payFollowUp: 0
  })

  const permissions = useMemo(() => getAdminSectionPermissions(normalizedAccessRole), [normalizedAccessRole])

  const sectionItems = useMemo(() => {
    return Object.values(ADMIN_SECTION_KEYS)
      .filter((sectionKey) => permissions.sections.has(sectionKey))
      .map((sectionKey) => ({ key: sectionKey, label: SECTION_LABELS[sectionKey] || sectionKey }))
  }, [permissions.sections])

  useEffect(() => {
    if (!permissions.sections.has(activeSection)) {
      setActiveSection(getDefaultAdminSectionForRole(normalizedAccessRole))
    }
  }, [activeSection, normalizedAccessRole, permissions.sections])

  useEffect(() => {
    if (!requestedSection) return
    const nextSection = String(requestedSection).trim().toLowerCase()
    if (!permissions.sections.has(nextSection)) return
    if (nextSection === activeSection) return
    setActiveSection(nextSection)
  }, [activeSection, permissions.sections, requestedSection])

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true)
    setDashboardError('')

    const today = new Date().toISOString().slice(0, 10)

    const fetchTimeEntries = async () => {
      const baseSelect = 'id, entry_date, clocked_in_at, clocked_out_at'
      const withTotal = `${baseSelect}, total_hours, approval_status`
      const withoutTotal = `${baseSelect}, approval_status`

      const response = await supabase
        .from('employee_time_entries')
        .select(withTotal)
        .order('updated_at', { ascending: false })
        .limit(400)

      if (response.error && isSchemaMismatchError(response.error)) {
        return supabase
          .from('employee_time_entries')
          .select(withoutTotal)
          .order('updated_at', { ascending: false })
          .limit(400)
      }

      return response
    }

    const [jobsResponse, assignmentsResponse, timeEntriesResponse, issuesResponse, payResponse] = await Promise.all([
      supabase.from('jobs').select('id, job_date, status').eq('job_date', today),
      supabase.from('job_worker_assignments').select('worker_id, job_id'),
      fetchTimeEntries(),
      supabase.from('admin_issues').select('id, resolution_status').order('updated_at', { ascending: false }).limit(400),
      supabase.from('pay_status_records').select('id, payment_status, dispute_flag').order('updated_at', { ascending: false }).limit(400)
    ])

    const assignmentsSchemaMismatch = isSchemaMismatchError(assignmentsResponse.error)
    const timeEntriesSchemaMismatch = isSchemaMismatchError(timeEntriesResponse.error)

    if (jobsResponse.error || (assignmentsResponse.error && !assignmentsSchemaMismatch) || (timeEntriesResponse.error && !timeEntriesSchemaMismatch)) {
      setDashboardLoading(false)
      setDashboardError(
        jobsResponse.error?.message || assignmentsResponse.error?.message || timeEntriesResponse.error?.message || 'Unable to load admin dashboard.'
      )
      return
    }

    const todaysJobs = jobsResponse.data || []
    const todaysJobIds = new Set(todaysJobs.map((job) => String(job.id)))

    const scheduledWorkers = new Set(
      ((assignmentsSchemaMismatch ? [] : assignmentsResponse.data) || [])
        .filter((row) => todaysJobIds.has(String(row.job_id)))
        .map((row) => String(row.worker_id))
        .filter(Boolean)
    )

    const entryRows = (timeEntriesResponse.data || []).filter((entry) => String(entry.entry_date || '').slice(0, 10) === today)
    const clockedInNow = entryRows.filter((entry) => entry.clocked_in_at && !entry.clocked_out_at).length
    const missingPunches = entryRows.filter((entry) => !entry.clocked_in_at || !entry.clocked_out_at).length
    const hoursAwaitingReview = entryRows.filter((entry) => ['pending', 'needs_review', 'disputed'].includes(String(entry.approval_status || '').toLowerCase())).length

    const unresolvedIssues = (issuesResponse.data || []).filter((issue) => !['resolved', 'closed'].includes(String(issue.resolution_status || '').toLowerCase())).length
    const payFollowUp = (payResponse.data || []).filter((record) => {
      const status = String(record.payment_status || '').toLowerCase()
      return ['unpaid', 'partial', 'disputed'].includes(status) || record.dispute_flag === true
    }).length

    setDashboardStats({
      todaysJobs: todaysJobs.length,
      scheduledWorkers: scheduledWorkers.size,
      clockedInNow,
      missingPunches,
      hoursAwaitingReview,
      unresolvedIssues,
      payFollowUp
    })

    setDashboardUpdatedAt(new Date().toISOString())
    setDashboardLoading(false)
  }, [])

  useEffect(() => {
    if (!canAccessAdminOps(normalizedAccessRole)) return
    loadDashboard()
  }, [loadDashboard, normalizedAccessRole])

  const canOpenSection = useCallback(
    (sectionKey) => canAccessAdminSection(normalizedAccessRole, sectionKey),
    [normalizedAccessRole]
  )

  if (!canAccessAdminOps(normalizedAccessRole)) {
    return (
      <ErrorState
        title="Admin ops unavailable"
        message="This role does not have access to admin operations tools."
      />
    )
  }

  const renderDashboardSection = () => {
    if (dashboardLoading) {
      return <LoadingState title="Loading admin dashboard" message="Compiling today's operational admin metrics." />
    }

    if (dashboardError) {
      return <ErrorState title="Dashboard unavailable" message={dashboardError} />
    }

    return (
      <section className="gg-module-stack">
        <section className="gg-module-panel">
          <SectionHeader
            eyebrow="Phase 1"
            title="Admin Operations Dashboard"
            description="Today’s jobs, staffing, time review, issues, and pay follow-up in one place."
          />
          <div className="gg-tool-pills">
            <StatPill label="Today's Jobs" value={dashboardStats.todaysJobs} tone={dashboardStats.todaysJobs ? 'info' : 'warning'} />
            <StatPill label="Scheduled Workers" value={dashboardStats.scheduledWorkers} tone={dashboardStats.scheduledWorkers ? 'info' : 'warning'} />
            <StatPill label="Clocked In" value={dashboardStats.clockedInNow} tone={dashboardStats.clockedInNow ? 'success' : 'info'} />
            <StatPill label="Missing Punches" value={dashboardStats.missingPunches} tone={dashboardStats.missingPunches ? 'warning' : 'info'} />
            <StatPill label="Hours Awaiting Review" value={dashboardStats.hoursAwaitingReview} tone={dashboardStats.hoursAwaitingReview ? 'warning' : 'info'} />
            <StatPill label="Unresolved Issues" value={dashboardStats.unresolvedIssues} tone={dashboardStats.unresolvedIssues ? 'warning' : 'info'} />
            <StatPill label="Pay Follow-up" value={dashboardStats.payFollowUp} tone={dashboardStats.payFollowUp ? 'warning' : 'info'} />
          </div>
          <p className="gg-text-soft">Last updated {formatDateTime(dashboardUpdatedAt)}</p>
          <div className="gg-form-actions">
            <button type="button" className="gg-button gg-button--secondary" onClick={loadDashboard}>Refresh Dashboard</button>
          </div>
        </section>

        <section className="gg-module-panel">
          <p className="gg-module-panel__label">Quick Repair Actions</p>
          <div className="gg-action-checklist">
            <article className="gg-action-checklist__item">
              <p className="gg-record-item__title">Fix bad punch</p>
              <p className="gg-record-item__meta">Open manager time admin to correct punch in/out and review stale punches.</p>
              <button type="button" className="gg-button gg-button--ghost" onClick={onOpenManagerTime}>Open Time Admin</button>
            </article>
            <article className="gg-action-checklist__item">
              <p className="gg-record-item__title">Fix worker-job assignment</p>
              <p className="gg-record-item__meta">Open jobs workspace to assign or unassign workers and update shift lead assignment.</p>
              <button type="button" className="gg-button gg-button--ghost" onClick={onOpenJobs}>Open Jobs</button>
            </article>
            <article className="gg-action-checklist__item">
              <p className="gg-record-item__title">Fix pay-status mismatch</p>
              <p className="gg-record-item__meta">Open time/pay section for visibility updates and dispute notes.</p>
              <button type="button" className="gg-button gg-button--ghost" onClick={() => setActiveSection(ADMIN_SECTION_KEYS.TIME_PAY)}>Open Time + Pay</button>
            </article>
            <article className="gg-action-checklist__item">
              <p className="gg-record-item__title">Repair identity binding</p>
              <p className="gg-record-item__meta">Open access tools to reconcile worker/profile links and access status history.</p>
              <button type="button" className="gg-button gg-button--ghost" onClick={() => setActiveSection(ADMIN_SECTION_KEYS.ACCESS)}>Open Access</button>
            </article>
            <article className="gg-action-checklist__item">
              <p className="gg-record-item__title">Open unresolved issues</p>
              <p className="gg-record-item__meta">Route into issue center and admin issue queue for follow-up.</p>
              <button type="button" className="gg-button gg-button--ghost" onClick={() => setActiveSection(ADMIN_SECTION_KEYS.ISSUES)}>Open Issues</button>
            </article>
          </div>
        </section>
      </section>
    )
  }

  const renderSection = () => {
    if (activeSection === ADMIN_SECTION_KEYS.DASHBOARD) return renderDashboardSection()

    if (activeSection === ADMIN_SECTION_KEYS.ACCESS) {
      return canOpenSection(ADMIN_SECTION_KEYS.ACCESS) ? <AdminIdentityCleanupPanel /> : <EmptyState title="Access denied" message="Role does not include access administration." />
    }

    if (activeSection === ADMIN_SECTION_KEYS.WORKFORCE) {
      return (
        <section className="gg-module-stack">
          <section className="gg-module-panel">
            <SectionHeader
              eyebrow="Admin"
              title="Worker / Job / Site Operations"
              description="Use existing end-to-end workspaces for create/edit/archive flows and assignment controls."
            />
            <div className="gg-action-checklist">
              <article className="gg-action-checklist__item">
                <p className="gg-record-item__title">Worker Admin Panel</p>
                <p className="gg-record-item__meta">Create/edit/archive workers, update work status and role mapping, and inspect correction context.</p>
                <button type="button" className="gg-button gg-button--ghost" onClick={onOpenWorkers}>Open Workers Workspace</button>
              </article>
              <article className="gg-action-checklist__item">
                <p className="gg-record-item__title">Job Management</p>
                <p className="gg-record-item__meta">Create/edit/cancel jobs, assign/unassign workers, set shift lead, and manage worker-visible notes.</p>
                <button type="button" className="gg-button gg-button--ghost" onClick={onOpenJobs}>Open Jobs Workspace</button>
              </article>
              <article className="gg-action-checklist__item">
                <p className="gg-record-item__title">Site Management</p>
                <p className="gg-record-item__meta">Add/edit/archive sites and keep operations-critical instructions in one profile.</p>
                <button type="button" className="gg-button gg-button--ghost" onClick={onOpenSites}>Open Sites Workspace</button>
              </article>
            </div>
          </section>
        </section>
      )
    }

    if (activeSection === ADMIN_SECTION_KEYS.CONTACTS) {
      return canOpenSection(ADMIN_SECTION_KEYS.CONTACTS) ? <ContactsAdminPanel onDataChanged={loadDashboard} /> : <EmptyState title="Access denied" message="Role does not include contact administration." />
    }

    if (activeSection === ADMIN_SECTION_KEYS.TIME_PAY) {
      return canOpenSection(ADMIN_SECTION_KEYS.TIME_PAY) ? (
        <section className="gg-module-stack">
          <section className="gg-module-panel">
            <p className="gg-module-panel__label">Time Review Workspace</p>
            <p className="gg-text-soft">Manager time administration remains server-authoritative; manual corrections require reason and are auditable.</p>
            <div className="gg-form-actions">
              <button type="button" className="gg-button gg-button--ghost" onClick={onOpenManagerTime}>Open Full Time Admin Workspace</button>
            </div>
          </section>
          <section className="gg-module-panel">
            <PayStatusAdminPanel onDataChanged={loadDashboard} />
          </section>
        </section>
      ) : (
        <EmptyState title="Access denied" message="Role does not include time/pay administration." />
      )
    }

    if (activeSection === ADMIN_SECTION_KEYS.ISSUES) {
      return canOpenSection(ADMIN_SECTION_KEYS.ISSUES) ? (
        <section className="gg-module-stack">
          <section className="gg-module-panel">
            <div className="gg-form-actions">
              <button type="button" className="gg-button gg-button--ghost" onClick={() => onOpenIssueCenter('', { sourceView: 'admin_ops' })}>
                Open Full Issue Center
              </button>
            </div>
          </section>
          <section className="gg-module-panel">
            <AdminIssuesPanel onDataChanged={loadDashboard} />
          </section>
        </section>
      ) : (
        <EmptyState title="Access denied" message="Role does not include issue management." />
      )
    }

    if (activeSection === ADMIN_SECTION_KEYS.CORRECTIONS) {
      return canOpenSection(ADMIN_SECTION_KEYS.CORRECTIONS) ? (
        <CorrectionCenterPanel
          onOpenIssueCenter={() => onOpenIssueCenter('', { sourceView: 'admin_ops' })}
          onOpenWorkers={onOpenWorkers}
          onOpenJobs={onOpenJobs}
          onOpenTimeAdmin={onOpenManagerTime}
          onOpenAccess={() => {
            if (canOpenSection(ADMIN_SECTION_KEYS.ACCESS)) {
              setActiveSection(ADMIN_SECTION_KEYS.ACCESS)
            } else {
              onOpenAccessTools?.()
            }
          }}
          onOpenPayStatus={() => setActiveSection(ADMIN_SECTION_KEYS.TIME_PAY)}
        />
      ) : (
        <EmptyState title="Access denied" message="Role does not include correction tools." />
      )
    }

    if (activeSection === ADMIN_SECTION_KEYS.AUDIT || activeSection === ADMIN_SECTION_KEYS.REPORTS || activeSection === ADMIN_SECTION_KEYS.SETTINGS) {
      return <AdminAuditReportsSettingsPanel />
    }

    if (activeSection === ADMIN_SECTION_KEYS.ANNOUNCEMENTS) {
      return canOpenSection(ADMIN_SECTION_KEYS.ANNOUNCEMENTS) ? (
        <section className="gg-module-panel gg-announcements-host">
          <AnnouncementsCenter />
        </section>
      ) : (
        <EmptyState title="Access denied" message="Role does not include announcements administration." />
      )
    }

    return <EmptyState title="Section unavailable" message="The selected admin section is not available for this role." />
  }

  return (
    <section className="gg-module-stack" data-testid="admin-operations-center">
      <section className="gg-module-panel">
        <SectionHeader
          eyebrow="Admin Ops"
          title="Phase 1 Admin Operations Center"
          description="Unified control plane for access, workforce setup, time review, pay visibility, issues, corrections, audit, reports, and settings."
        />
        <p className="gg-text-soft">Access role {formatAccessRoleLabel(normalizedAccessRole)} • Workspace role {formatAccessRoleLabel(workspaceRole || normalizedAccessRole)} • Worker {userWorkerId || 'Not bound'}</p>
      </section>

      <section className="gg-module-panel">
        <div className="gg-quick-action-grid">
          {sectionItems.map((section) => (
            <button
              key={section.key}
              type="button"
              className={`gg-quick-action ${activeSection === section.key ? 'gg-nav-link--active' : ''}`}
              onClick={() => setActiveSection(section.key)}
            >
              <span className="gg-quick-action__title">{section.label}</span>
              <span className="gg-quick-action__desc">Open {section.label} tools</span>
            </button>
          ))}
        </div>
      </section>

      <section className="gg-module-panel">
        {renderSection()}
      </section>
    </section>
  )
}
